'use client'

/**
 * Mobile header — sticky top, left = logo (or back), right = actions (search + account).
 *
 * Slots:
 *   - Title: pages may render <MobileHeaderTitle> into the slot (rare); wordmark stays visible.
 *   - On `/campaigns` (guests): trailing is Sign up for free only — Log in lives on /signup.
 *
 * Desktop never sees this — the parent lobby layout wraps this in `lg:hidden`.
 */

import { useCallback } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { HugeiconsIcon } from '@hugeicons/react'
import { SearchIcon, Menu01Icon, BubbleChatSpark01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useCachedUser } from '@/hooks/useCachedUser'
import {
  useHasMobileHeaderTitle,
  useHasMobileHeaderActions,
  useMobileHeaderTitleSlotRef,
  useMobileHeaderActionsSlotRef,
} from '@/contexts/MobileHeaderContext'

export type MobileHeaderProps = {
  /** Optional left-side back chevron handler (used by chat panel header). */
  onBack?: () => void
  /** Open the command bar. Wired to the search icon by default. */
  onOpenSearch?: () => void
  /** Open the account sheet. Wired to the avatar button by default. */
  onOpenAccount?: () => void
  /** Avatar element for the account button (current user). Optional — fallback to icon. */
  accountAvatar?: React.ReactNode
  /** Hide trailing default actions (used on chat panel where headers are minimal). */
  hideDefaultActions?: boolean
  /** Override what's rendered as the leading element (logo / title). Used by chat back button. */
  leadingOverride?: React.ReactNode
  /** Open the sidebar overlay. Renders a hamburger on non-home/campaigns routes. */
  onOpenSidebar?: () => void
  /** Open the chat. Wired to the wordmark tap when no back/hamburger is shown. */
  onOpenChat?: () => void
  /**
   * When true, this header does NOT register its title/actions divs as the
   * MobileHeaderProvider's portal slots. Use for headers that should never
   * receive page-supplied titles (e.g. the chat pane header) so they can
   * coexist with the App pane header without slot collision.
   */
  disableSlots?: boolean
}

export function MobileHeader({
  onBack,
  onOpenSearch,
  onOpenAccount,
  accountAvatar,
  hideDefaultActions = false,
  leadingOverride,
  disableSlots = false,
  onOpenSidebar,
  onOpenChat,
}: MobileHeaderProps) {
  const pathname = usePathname() ?? ''
  const { isLoggedIn } = useCachedUser()
  const titleSlotRef = useMobileHeaderTitleSlotRef()
  const actionsSlotRef = useMobileHeaderActionsSlotRef()
  const noopRef = (_el: HTMLElement | null) => {}

  const pageHasTitle = useHasMobileHeaderTitle()
  const pageHasActions = useHasMobileHeaderActions()
  const isCampaigns =
    pathname === '/campaigns' || pathname.startsWith('/campaigns/')
  const showGuestHeaderAuth = !isLoggedIn && !pageHasActions && isCampaigns

  const handleSearch = useCallback(() => {
    onOpenSearch?.()
  }, [onOpenSearch])
  const handleAccount = useCallback(() => {
    onOpenAccount?.()
  }, [onOpenAccount])

  return (
    <header
      data-mobile-header
      className={cn(
        'sticky top-0 z-30 flex w-full shrink-0 items-center gap-2 border-b border-atrium-border/50 bg-atrium-background px-3',
        'h-14',
        // iOS notch — pad the top with the safe-area inset.
        'pt-[env(safe-area-inset-top)] [&]:h-[calc(theme(spacing.14)+env(safe-area-inset-top))]'
      )}
    >
      {/* Leading: back button | logo (same wordmark as home on all routes) | portal title */}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {leadingOverride ? (
          leadingOverride
        ) : onBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Back"
            onClick={onBack}
            className="-ml-1 size-9 shrink-0"
          >
            {/* Lucide-free chevron via SVG, no extra deps */}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </Button>
        ) : !isCampaigns && onOpenSidebar ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            onClick={onOpenSidebar}
            className="-ml-1 size-9 shrink-0"
          >
            <HugeiconsIcon icon={Menu01Icon} className="size-5" />
          </Button>
        ) : (
          <button
            type="button"
            onClick={onOpenChat ?? undefined}
            aria-label="Open chat"
            className="flex items-center rounded outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
          >
            <Logo
              variant="wordmark"
              width={76}
              height={22}
              className="h-[22px] w-auto max-w-[136px] shrink-0 object-contain object-left"
            />
          </button>
        )}

        {/* Page-supplied title slot (portaled into) */}
        <div
          ref={disableSlots ? noopRef : titleSlotRef}
          data-mobile-header-title-slot
          className={cn(
            'min-w-0 flex-1 truncate text-[17px] font-semibold text-atrium-black',
            // Hide the slot wrapper when empty; keep the div so portals have a stable target.
            pageHasTitle ? 'block' : 'hidden'
          )}
        />
      </div>

      {/* Trailing: page-supplied actions (portal) OR default search + account */}
      <div className="flex shrink-0 items-center gap-1">
        <div
          ref={disableSlots ? noopRef : actionsSlotRef}
          data-mobile-header-actions-slot
          className={cn('flex items-center gap-1', pageHasActions ? 'flex' : 'hidden')}
        />
        {!pageHasActions && !hideDefaultActions ? (
          <>
            {showGuestHeaderAuth ? (
              <Button asChild variant="atrium" size="sm" className="h-8 max-w-[11.5rem] shrink-0 px-2.5 text-[11px] leading-tight sm:h-9 sm:max-w-none sm:px-3 sm:text-sm">
                <Link href="/signup" prefetch={false} className="truncate">
                  Sign up for free
                </Link>
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Search"
              onClick={handleSearch}
              className="size-9"
            >
              <HugeiconsIcon icon={SearchIcon} className="size-5" />
            </Button>
            {onOpenChat ? (
              <button
                type="button"
                aria-label="Open chat"
                onClick={onOpenChat}
                className="flex size-9 shrink-0 items-center justify-center rounded-full transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
              >
                <span className="flex size-8 items-center justify-center rounded-full bg-accent-700 shadow-sm">
                  <HugeiconsIcon icon={BubbleChatSpark01Icon} className="size-[18px] text-white" />
                </span>
              </button>
            ) : null}
            <button
              type="button"
              aria-label="Account"
              onClick={handleAccount}
              className="flex size-10 shrink-0 items-center justify-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
            >
              {accountAvatar ?? (
                <div className="size-8 rounded-full bg-atrium-border/60" aria-hidden />
              )}
            </button>
          </>
        ) : null}
      </div>
    </header>
  )
}
